import { useState, useEffect, useMemo, useCallback } from 'react';
import { Drawer, Button, Table, Tag, Space, Empty, Modal, Form, Switch, InputNumber, message, Input, Row, Col, Alert } from 'antd';
import { conferenceBenefitList, deleteConferenceBenefit, updateConferenceBenefit } from '@services/Conference';
import config from '@/config';
import './BenefitManageDrawer.less';
import SelectProjectBenefitsDrawer from './SelectProjectBenefitsDrawer';
import { BENEFIT_CATEGORIES, BENEFIT_CATEGORY_MAP } from '../constants';

/** 权益分类 id 列表（按 BENEFIT_CATEGORIES 顺序） */
const TYPE_KEYS = BENEFIT_CATEGORIES.map(c => c.id);

/** 权益分类 id -> 名称映射（取用 constants 统一数据） */
const TYPE_MAP = BENEFIT_CATEGORY_MAP;

/** 解析展位号 */
function parseBoothNumbers(boothNumbersStr) {
    if (!boothNumbersStr) return [];
    try {
        const parsed = JSON.parse(boothNumbersStr);
        return Array.isArray(parsed) ? parsed : [];
    } catch {
        return [];
    }
}

/** 清理展位号中的转义引号 */
function cleanBoothNumber(boothNumber) {
    if (!boothNumber) return '';
    return String(boothNumber).replace(/^"|"$/g, '');
}

/** 获取所有展位权益的展位号集合（用于重复校验） */
function getAllBoothNumbersSet(benefits, excludeBenefitId) {
    const allBooths = new Set();
    for (const b of benefits) {
        if (b.isBoothBenefit === 1 && b.benefitId !== excludeBenefitId && b.boothNumbers) {
            const booths = parseBoothNumbers(b.boothNumbers);
            for (const booth of booths) {
                allBooths.add(booth);
            }
        }
    }
    return allBooths;
}

/** 单个权益表格 */
function BenefitTable({ data, onEdit, onRemove, onConfigBooth, onSoldDetail }) {
    const columns = [
        {
            title: '序号',
            width: 60,
            render: (_, __, index) => index + 1,
        },
        {
            title: '权益名称',
            dataIndex: 'benefitName',
            render: (text, record) => {
                const tags = [];
                if (record.isBoothBenefit === 1) {
                    tags.push(
                        <Tag key="booth" color="orange" size="small">
                            展位权益
                        </Tag>,
                    );
                }
                if (record.builtin === 0) {
                    tags.push(
                        <Tag key="custom" color="blue" size="small">
                            自定义权益
                        </Tag>,
                    );
                }
                return (
                    <div className="benefit-name-cell">
                        <div className="benefit-name-title">
                            {text}
                            {tags.length > 0 && <span style={{ marginLeft: 4 }}>{tags}</span>}
                        </div>
                        <div className="benefit-name-sub">权益说明：{record.benefitDesc || '-'}</div>
                        <div className="benefit-name-sub">价格参照：{record.priceReference || '-'}</div>
                    </div>
                );
            },
        },
        {
            title: '权益总量',
            dataIndex: 'quantity',
            width: 100,
            render: (value, record) => {
                if (record.isBoothBenefit === 1) {
                    if (parseBoothNumbers(record.boothNumbers).length === 0) {
                        return <span className="booth-unconfigured">未配置展位号</span>;
                    }
                    return value || 0;
                }
                return value === 0 ? '无限制' : value;
            },
        },
        {
            title: '已售数量',
            dataIndex: 'soldCount',
            width: 100,
            render: (value, record) => {
                if (record.isBoothBenefit === 1 && value > 0) {
                    return (
                        <span className="sold-count-link" onClick={() => onSoldDetail(record)}>
                            {value}
                        </span>
                    );
                }
                return <span className="sold-count-text">{value || 0}</span>;
            },
        },
        {
            title: '操作',
            width: 180,
            render: (_, record) => (
                <Space size="small">
                    {record.isBoothBenefit === 1 ? (
                        <Button type="link" size="small" onClick={() => onConfigBooth(record)}>
                            编辑
                        </Button>
                    ) : (
                        <Button type="link" size="small" onClick={() => onEdit(record)}>
                            编辑
                        </Button>
                    )}
                    <Button type="link" size="small" danger onClick={() => onRemove(record)}>
                        移除
                    </Button>
                </Space>
            ),
        },
    ];

    if (!data || data.length === 0) {
        return (
            <div className="benefit-empty">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="未添加权益" />
            </div>
        );
    }

    return <Table className="benefit-table" columns={columns} dataSource={data} rowKey="id" pagination={false} size="small" bordered />;
}

/** 已售详情弹窗 */
function SoldBoothDetailModal({ open, record, onClose }) {
    const columns = [
        { title: '展位编号', dataIndex: 'boothNumber', render: v => cleanBoothNumber(v) },
        { title: '已售机构', dataIndex: 'sponsorName' },
    ];

    const data = useMemo(() => {
        if (!record) return [];
        const soldMap = new Map();
        (record.soldBoothList || []).forEach(item => {
            const key = cleanBoothNumber(item.boothNumber);
            soldMap.set(key, item.sponsorName || '-');
        });

        const allBooths = parseBoothNumbers(record.boothNumbers);
        return allBooths.map((booth, idx) => ({
            boothNumber: booth,
            sponsorName: soldMap.get(cleanBoothNumber(booth)) || '-',
            key: `booth-${idx}`,
        }));
    }, [record]);

    return (
        <Modal title="已售详情" open={open} onCancel={onClose} footer={null} zIndex={1300}>
            <div style={{ marginBottom: 12 }}>
                <b>权益名称：</b>
                {record?.benefitName || '-'}
            </div>
            <div style={{ maxHeight: '50vh', overflowY: 'auto' }}>
                <Table columns={columns} dataSource={data} rowKey="key" pagination={false} size="small" bordered />
            </div>
        </Modal>
    );
}

/** 新增展位编号弹窗 */
function AddBoothModal({ open, onClose, onConfirm }) {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (open) setValue('');
    }, [open]);

    const handleOk = () => {
        const lines = value
            .split('\n')
            .map(s => s.trim())
            .filter(s => s.length > 0);
        if (lines.length === 0) {
            message.warning('请输入展位编号');
            return;
        }
        if (lines.length > 1000) {
            message.warning('一次最多添加1000个展位编号');
            return;
        }
        onConfirm(lines);
    };

    return (
        <Modal title="新增展位编号" open={open} onOk={handleOk} onCancel={onClose} okText="确认" cancelText="取消" zIndex={1400}>
            <div style={{ marginBottom: 8, color: '#faad14' }}>*展位编号（一行一个展位编号，一次最多添加1000个）</div>
            <Input.TextArea rows={8} value={value} onChange={e => setValue(e.target.value)} placeholder="A01&#10;A02&#10;A03" />
        </Modal>
    );
}

/** 编辑展位编号弹窗 */
function EditBoothModal({ open, currentValue, onClose, onConfirm }) {
    const [value, setValue] = useState('');

    useEffect(() => {
        if (open) setValue('');
    }, [open]);

    const handleOk = () => {
        const trimmed = value.trim();
        if (!trimmed) {
            message.warning('请输入新展位编号');
            return;
        }
        onConfirm(trimmed);
    };

    return (
        <Modal title="编辑展位号" open={open} onOk={handleOk} onCancel={onClose} okText="确认" cancelText="取消" zIndex={1400}>
            <div style={{ paddingTop: 20, marginBottom: 16 }}>
                <b>当前展位编号：</b>
                {currentValue}
            </div>
            <div style={{ marginBottom: 8 }}>新展位编号：</div>
            <Input value={value} onChange={e => setValue(e.target.value)} placeholder="请输入新展位编号" />
        </Modal>
    );
}

/** 配置展位编号弹窗（标题为“编辑”：展位编号维护 + 权益说明 / 价格参照） */
function ConfigBoothModal({ open, record, benefits, conferenceId, onClose, onSuccess }) {
    const [form] = Form.useForm();
    const [boothList, setBoothList] = useState([]);
    const [originalBoothList, setOriginalBoothList] = useState([]);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editIndex, setEditIndex] = useState(-1);
    const [saving, setSaving] = useState(false);

    const watchedDesc = Form.useWatch('benefitDesc', form);
    const watchedPrice = Form.useWatch('priceReference', form);

    // 打开时解析展位号，并回填权益说明 / 价格参照
    useEffect(() => {
        if (open && record) {
            const list = parseBoothNumbers(record.boothNumbers);
            setBoothList(list);
            setOriginalBoothList(list);
            form.setFieldsValue({
                benefitDesc: record.benefitDesc || '',
                priceReference: record.priceReference || '',
            });
        }
    }, [open, record, form]);

    const hasChanged = useMemo(() => {
        const boothChanged = JSON.stringify(boothList) !== JSON.stringify(originalBoothList);
        const descChanged = (watchedDesc || '') !== (record?.benefitDesc || '');
        const priceChanged = (watchedPrice || '') !== (record?.priceReference || '');
        return boothChanged || descChanged || priceChanged;
    }, [boothList, originalBoothList, watchedDesc, watchedPrice, record]);

    const allBoothsSet = useMemo(() => {
        return getAllBoothNumbersSet(benefits, record?.benefitId);
    }, [benefits, record]);

    const soldBoothSet = useMemo(() => {
        const set = new Set();
        (record?.soldBoothList || []).forEach(item => {
            set.add(cleanBoothNumber(item.boothNumber));
        });
        return set;
    }, [record]);

    const handleAddConfirm = newBooths => {
        // 检查重复（当前列表 + 其他权益的展位号）
        const currentSet = new Set(boothList);
        const duplicates = [];
        newBooths.forEach(booth => {
            if (currentSet.has(booth) || allBoothsSet.has(booth)) {
                duplicates.push(booth);
            }
        });
        if (duplicates.length > 0) {
            message.error(`以下展位编号已存在：${duplicates.join('、')}`);
            return;
        }
        setBoothList(prev => [...prev, ...newBooths]);
        setAddModalOpen(false);
    };

    const handleEditConfirm = newValue => {
        const oldValue = boothList[editIndex];
        if (newValue === oldValue) {
            setEditModalOpen(false);
            return;
        }
        // 检查重复
        const currentSet = new Set(boothList);
        currentSet.delete(oldValue);
        if (currentSet.has(newValue) || allBoothsSet.has(newValue)) {
            message.error(`展位编号「${newValue}」已存在`);
            return;
        }
        setBoothList(prev => {
            const next = [...prev];
            next[editIndex] = newValue;
            return next;
        });
        setEditModalOpen(false);
    };

    const handleDelete = index => {
        const booth = boothList[index];
        if (soldBoothSet.has(booth)) {
            message.warning('该展位号已有机构选择，不可删除');
            return;
        }
        Modal.confirm({
            title: '提示',
            content: '确认删除该展位',
            okText: '确认',
            cancelText: '取消',
            onOk: () => {
                setBoothList(prev => prev.filter((_, i) => i !== index));
            },
        });
    };

    const handleSave = async () => {
        const values = await form.validateFields();
        setSaving(true);
        const param = {
            benefitId: record.benefitId,
            boothNumbers: JSON.stringify(boothList),
            conferenceId: Number(conferenceId),
            id: record.id,
            quantity: boothList.length,
            benefitDesc: values.benefitDesc || '',
            priceReference: values.priceReference || '',
        };
        const res = await updateConferenceBenefit(param);
        setSaving(false);
        if (res.status_code === 200) {
            message.success('保存成功');
            onSuccess();
            onClose();
        } else {
            message.error(res.message || '保存失败');
        }
    };

    const handleClose = () => {
        if (hasChanged) {
            Modal.confirm({
                title: '提示',
                content: '发现有数据变动，确认不保存并关闭？',
                okText: '确认',
                cancelText: '取消',
                onOk: () => {
                    setBoothList(originalBoothList);
                    form.setFieldsValue({
                        benefitDesc: record?.benefitDesc || '',
                        priceReference: record?.priceReference || '',
                    });
                    onClose();
                },
            });
        } else {
            onClose();
        }
    };

    const columns = [
        { title: '展位编号', dataIndex: 'boothNumber', render: v => v },
        {
            title: '操作',
            width: 120,
            render: (_, __, index) => {
                const booth = boothList[index];
                const isSold = soldBoothSet.has(booth);
                return (
                    <Space size="small">
                        <Button
                            type="link"
                            size="small"
                            disabled={isSold}
                            onClick={() => {
                                setEditIndex(index);
                                setEditModalOpen(true);
                            }}
                        >
                            编辑
                        </Button>
                        <Button type="link" size="small" danger disabled={isSold} onClick={() => handleDelete(index)}>
                            删除
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <>
            <Modal
                title="编辑权益"
                open={open}
                onCancel={handleClose}
                footer={
                    <div className="config-booth-footer">
                        <Button onClick={handleClose}>取消</Button>
                        <Button type="primary" loading={saving} onClick={handleSave}>
                            保存
                        </Button>
                    </div>
                }
                width={600}
                zIndex={1300}
            >
                <div style={{ maxHeight: '60vh', overflowY: 'auto', paddingRight: 8 }}>
                    <Form form={form} layout="horizontal" labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} autoComplete="off">
                        <Form.Item label="权益分类">
                            <span>{record?.typeName || '-'}</span>
                        </Form.Item>
                        <Form.Item label="权益名称">
                            <span>{record?.benefitName || '-'}</span>
                        </Form.Item>
                        <Form.Item label="权益说明" name="benefitDesc" extra={<span className="benefit-field-hint">说明参考：{record?.benefitDesc || '-'}</span>}>
                            <Input.TextArea maxLength={500} showCount autoSize={{ minRows: 2, maxRows: 4 }} placeholder="请输入权益说明" />
                        </Form.Item>
                        <Form.Item label="价格参照" name="priceReference" extra={<span className="benefit-field-hint">平台大会建议：{record?.priceReference || '-'}，可视本项目会议规模上下浮动</span>}>
                            <Input maxLength={200} showCount placeholder="请输入价格参照" />
                        </Form.Item>
                    </Form>
                    <div style={{ marginBottom: 12 }}>
                        <b>展位编号：</b>
                        <Button type="link" size="small" onClick={() => setAddModalOpen(true)}>
                            <i className="bi bi-plus-circle" /> 新增展位编号
                        </Button>
                    </div>
                    <Table columns={columns} dataSource={boothList.map((b, i) => ({ boothNumber: b, key: i }))} pagination={false} size="small" bordered />
                </div>
            </Modal>

            <AddBoothModal open={addModalOpen} onClose={() => setAddModalOpen(false)} onConfirm={handleAddConfirm} />

            <EditBoothModal open={editModalOpen} currentValue={editIndex >= 0 ? boothList[editIndex] : ''} onClose={() => setEditModalOpen(false)} onConfirm={handleEditConfirm} />
        </>
    );
}

/** 编辑权益弹窗（非展位权益：权益说明 / 价格参照 / 可售数量），数据均取本会议已选权益 record */
function EditBenefitModal({ open, record, conferenceId, onClose, onSuccess }) {
    const [form] = Form.useForm();
    const [limited, setLimited] = useState(false);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (open && record) {
            const hasLimit = record.quantity > 0;
            setLimited(hasLimit);
            form.setFieldsValue({
                limited: hasLimit,
                quantity: hasLimit ? record.quantity : undefined,
                // 权益说明 / 价格参照：取本会议已选权益自身内容
                benefitDesc: record.benefitDesc || '',
                priceReference: record.priceReference || '',
            });
        }
    }, [open, record, form]);

    const handleOk = async () => {
        const values = await form.validateFields();
        setLoading(true);

        const param = {
            benefitId: record.benefitId,
            conferenceId: Number(conferenceId),
            id: record.id,
            quantity: values.limited ? values.quantity : 0,
            benefitDesc: values.benefitDesc || '',
            priceReference: values.priceReference || '',
        };

        const res = await updateConferenceBenefit(param);
        setLoading(false);

        if (res.status_code === 200) {
            message.success('更新成功');
            onSuccess();
            onClose();
        } else {
            message.error(res.message || '更新失败');
        }
    };

    const handleLimitedChange = checked => {
        setLimited(checked);
        if (!checked) {
            form.setFieldsValue({ quantity: undefined });
        }
    };

    return (
        <Modal title="编辑权益" open={open} onOk={handleOk} onCancel={onClose} confirmLoading={loading} okText="确认" cancelText="取消" zIndex={1200}>
            <Form form={form} layout="horizontal" labelCol={{ span: 5 }} wrapperCol={{ span: 19 }} autoComplete="off">
                <Form.Item label="权益分类">
                    <span>{record?.typeName || '-'}</span>
                </Form.Item>
                <Form.Item label="权益名称">
                    <span>{record?.benefitName || '-'}</span>
                </Form.Item>
                <Form.Item label="权益说明" name="benefitDesc" extra={<span className="benefit-field-hint">说明参考：{record?.benefitDesc || '-'}</span>}>
                    <Input.TextArea maxLength={500} showCount autoSize={{ minRows: 2, maxRows: 4 }} placeholder="请输入权益说明" />
                </Form.Item>
                <Form.Item label="价格参照" name="priceReference" extra={<span className="benefit-field-hint">平台大会建议：{record?.priceReference || '-'}，可视本项目会议规模上下浮动</span>}>
                    <Input maxLength={200} showCount placeholder="请输入价格参照" />
                </Form.Item>
                <Form.Item label="可售数量" required>
                    <Space align="center">
                        <Form.Item name="limited" noStyle valuePropName="checked">
                            <Switch checkedChildren="有限制" unCheckedChildren="无限制" onChange={handleLimitedChange} />
                        </Form.Item>
                        {limited && (
                            <Form.Item name="quantity" noStyle rules={[{ required: true, message: '请输入可售数量' }]}>
                                <InputNumber min={1} precision={0} placeholder="请输入可售数量" style={{ width: 200 }} />
                            </Form.Item>
                        )}
                    </Space>
                </Form.Item>
            </Form>
        </Modal>
    );
}

export default function BenefitManageDrawer({ open, onClose, conferenceId }) {
    const [_loading, setLoading] = useState(false);
    const [benefits, setBenefits] = useState([]);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [editRecord, setEditRecord] = useState(null);
    const [soldDetailOpen, setSoldDetailOpen] = useState(false);
    const [soldDetailRecord, setSoldDetailRecord] = useState(null);
    const [configBoothOpen, setConfigBoothOpen] = useState(false);
    const [configBoothRecord, setConfigBoothRecord] = useState(null);
    const [selectDrawerOpen, setSelectDrawerOpen] = useState(false);

    // 按类型分组，并按正序排列（createdAt 有效时按其升序，缺失/相同则以 id 升序兜底）
    const groupedBenefits = useMemo(() => {
        const groups = {};
        TYPE_KEYS.forEach(key => {
            groups[key] = [];
        });
        benefits.forEach(item => {
            const type = item.type;
            if (groups[type]) {
                groups[type].push(item);
            }
        });
        const timeOf = b => {
            const t = b.createdAt ? new Date(b.createdAt).getTime() : NaN;
            return Number.isNaN(t) ? null : t;
        };
        TYPE_KEYS.forEach(key => {
            groups[key].sort((a, b) => {
                const ta = timeOf(a);
                const tb = timeOf(b);
                if (ta !== null && tb !== null && ta !== tb) return ta - tb;
                return (a.id || 0) - (b.id || 0);
            });
        });
        return groups;
    }, [benefits]);

    // 获取权益列表
    const fetchBenefits = useCallback(async () => {
        if (!conferenceId) return;
        setLoading(true);
        const res = await conferenceBenefitList({ conferenceId: Number(conferenceId) });
        if (res.status_code === 200) {
            setBenefits(res.data || []);
        } else {
            setBenefits([]);
        }
        setLoading(false);
    }, [conferenceId]);

    useEffect(() => {
        if (open && conferenceId) {
            fetchBenefits();
        }
    }, [open, conferenceId, fetchBenefits]);

    // 导出商务函权益
    const handleExportBenefits = () => {
        window.location.href = `${config.API_PREFIX}/conference/boss/conferenceBenefit/export?conferenceId=${conferenceId}`;
    };

    const handleEdit = record => {
        setEditRecord(record);
        setEditModalOpen(true);
    };

    const handleRemove = record => {
        if (record.soldCount > 0) {
            message.warning('当前权益已有机构选择，不可删除');
            return;
        }

        Modal.confirm({
            title: '提示',
            content: '确认移除该权益',
            okText: '确认',
            cancelText: '取消',
            onOk: async () => {
                const res = await deleteConferenceBenefit({
                    benefitId: record.benefitId,
                    conferenceId: Number(conferenceId),
                });
                if (res.status_code === 200) {
                    message.success('移除成功');
                    fetchBenefits();
                } else {
                    message.error(res.message || '移除失败');
                }
            },
        });
    };

    const handleConfigBooth = record => {
        setConfigBoothRecord(record);
        setConfigBoothOpen(true);
    };

    const handleSoldDetail = record => {
        setSoldDetailRecord(record);
        setSoldDetailOpen(true);
    };

    const rowColProps = {
        xs: { span: 24 },
        sm: { span: 24 },
        md: { span: 20 },
        lg: { span: 18 },
        xl: { span: 14 },
        xxl: { span: 12 },
    };

    return (
        <>
            <Drawer title="权益管理" placement="bottom" size="100%" open={open} onClose={onClose} className="benefit-manage-drawer" destroyOnHidden maskClosable={onClose} footer={null} closable={{ placement: 'end' }}>
                <div className="benefit-drawer-content">
                    <Row justify="center">
                        <Col {...rowColProps}>
                            <Alert title="该页面为本项目已选“商务函”权益，若需新增权益请点击选择项目权益" type="warning" style={{ marginBottom: 16 }}></Alert>

                            <div className="benefit-drawer-header">
                                <Button type="primary" onClick={() => setSelectDrawerOpen(true)}>
                                    选择项目权益
                                </Button>
                                <Button type="primary" onClick={handleExportBenefits}>
                                    导出商务函权益
                                </Button>
                                {/* <div className="benefit-drawer-tip"></div> */}
                            </div>

                            {/* 按分类展示权益 */}
                            <div className="benefit-category-list">
                                {TYPE_KEYS.map(type => {
                                    const items = groupedBenefits[type] || [];
                                    const typeName = TYPE_MAP[type];
                                    return (
                                        <div key={type} className="benefit-category-section">
                                            <div className="benefit-category-title">
                                                {typeName}（{items.length}项）
                                            </div>
                                            <BenefitTable data={items} onEdit={handleEdit} onRemove={handleRemove} onConfigBooth={handleConfigBooth} onSoldDetail={handleSoldDetail} />
                                        </div>
                                    );
                                })}
                            </div>
                        </Col>
                    </Row>
                </div>
            </Drawer>

            {/* 编辑权益弹窗 */}
            <EditBenefitModal
                open={editModalOpen}
                record={editRecord}
                conferenceId={conferenceId}
                onClose={() => {
                    setEditModalOpen(false);
                    setEditRecord(null);
                }}
                onSuccess={fetchBenefits}
            />

            {/* 已售详情弹窗 */}
            <SoldBoothDetailModal
                open={soldDetailOpen}
                record={soldDetailRecord}
                onClose={() => {
                    setSoldDetailOpen(false);
                    setSoldDetailRecord(null);
                }}
            />

            {/* 配置展位编号弹窗 */}
            <ConfigBoothModal
                open={configBoothOpen}
                record={configBoothRecord}
                benefits={benefits}
                conferenceId={conferenceId}
                onClose={() => {
                    setConfigBoothOpen(false);
                    setConfigBoothRecord(null);
                }}
                onSuccess={fetchBenefits}
            />

            {/* 选择项目权益弹窗 */}
            <SelectProjectBenefitsDrawer open={selectDrawerOpen} onClose={() => setSelectDrawerOpen(false)} conferenceId={conferenceId} onSave={() => fetchBenefits()} />
        </>
    );
}
